// LA VENTA EN CURSO, GUARDADA EN ESTE DISPOSITIVO · TC-DR-AT (escenario 3).
//
// EL PROBLEMA. El carrito del punto de venta vivía solo en la memoria de
// la pantalla: salir de ella —con atrás, por el menú, por una recarga— lo
// vaciaba, con el cliente enfrente. Ahora se guarda aquí en cada cambio y
// se recupera al volver a entrar.
//
// DE QUIÉN ES. La llave lleva la empresa y la persona, y el valor lleva el
// turno de caja: en un mostrador compartido, el carrito de una persona no
// le aparece a otra, y el de un turno que ya se cerró no aparece en el
// siguiente (se borra en el momento en que se intenta leer).
//
// LO MEDIDO (3 de octubre de 2026). La sesión guardada (`auth.jsx`,
// `aplicarSesion`) trae `id`, `nombre`, `rol` y `tipo_cuenta`: NO trae un
// identificador de empresa, porque la respuesta del acceso no lo manda. El
// `id` de la persona es único entre empresas (lo mismo en lo que se apoya
// `cacheTrabajo.js`), así que hoy la parte de empresa de la llave vale «-»
// y el aislamiento lo da la persona. Si un día la sesión trae
// `id_corporacion` o `id_empresa`, entra en la llave sin tocar nada más.
// El turno sí trae identificador: `turno.id` (`TurnoSalida.id`).
//
// DÓNDE. En `localStorage`, que sobrevive a cerrar la pestaña: una venta a
// medias tiene que seguir ahí si el teléfono recarga la página. No guarda
// ni el lote ni el aviso de caducidad —se vuelven a consultar— ni nada de
// la forma de pago.
//
// NUNCA LANZA. Una ventana privada o un almacenamiento lleno hacen que
// estas llamadas fallen: se pierde el borrador, no la venta.

const PREFIJO = 'tego_borrador_venta';
const VERSION = 1;

function almacen() {
  try { return globalThis.localStorage || null; } catch { return null; }
}

function empresaDe(usuario) {
  const e = usuario?.id_corporacion ?? usuario?.id_empresa;
  return e === undefined || e === null || e === '' ? '-' : String(e);
}

/** La llave del borrador de esta persona, o `null` si no hay persona. */
export function llaveBorradorVenta(usuario) {
  const id = usuario?.id;
  if (id === undefined || id === null || id === '') return null;
  return `${PREFIJO}:${empresaDe(usuario)}:${id}`;
}

const idDe = (turno) => (turno === undefined || turno === null || turno === '' ? null : String(turno));

function renglon(r) {
  return {
    sku: r.sku,
    nombre: r.nombre,
    cantidad: Number(r.cantidad),
    precio: Number(r.precio),
    sinPrecio: !!r.sinPrecio,
    descuento: r.descuento || null,
  };
}

function renglonValido(r) {
  return !!r && typeof r.sku === 'string' && r.sku !== ''
    && Number.isFinite(Number(r.cantidad)) && Number(r.cantidad) > 0
    && Number.isFinite(Number(r.precio));
}

/**
 * Guarda la venta en curso. Con el carrito vacío, borra lo guardado.
 * Devuelve `true` si quedó escrita.
 */
export function guardarBorradorVenta(usuario, { turno = null, carrito = [], descuentoTotal = null,
                                                ubicacion = '', ubicacionElegida = false } = {}) {
  const llave = llaveBorradorVenta(usuario);
  const caja = almacen();
  if (!llave || !caja) return false;
  if (!Array.isArray(carrito) || carrito.length === 0) {
    borrarBorradorVenta(usuario);
    return false;
  }
  try {
    caja.setItem(llave, JSON.stringify({
      v: VERSION,
      turno: idDe(turno),
      guardado: new Date().toISOString(),
      carrito: carrito.map(renglon),
      descuentoTotal: descuentoTotal || null,
      ubicacion: ubicacion || '',
      ubicacionElegida: !!ubicacionElegida,
    }));
    return true;
  } catch {
    // Almacenamiento lleno o bloqueado: se sigue vendiendo sin borrador.
    return false;
  }
}

/**
 * Lo guardado para esta persona y este turno, o `null`.
 *
 * El borrador de OTRO turno se borra aquí mismo: esa caja ya se cerró.
 */
export function leerBorradorVenta(usuario, turno = null) {
  const llave = llaveBorradorVenta(usuario);
  const caja = almacen();
  if (!llave || !caja) return null;
  let crudo;
  try { crudo = caja.getItem(llave); } catch { return null; }
  if (!crudo) return null;
  let b;
  try { b = JSON.parse(crudo); } catch { b = null; }
  const sano = b && b.v === VERSION && Array.isArray(b.carrito)
    && b.carrito.length > 0 && b.carrito.every(renglonValido);
  if (!sano || idDe(b.turno) !== idDe(turno)) {
    borrarBorradorVenta(usuario);
    return null;
  }
  return {
    // `lote: undefined` hace que la pantalla vuelva a preguntar de qué
    // lote sale cada producto.
    carrito: b.carrito.map((r) => ({ ...renglon(r), lote: undefined })),
    descuentoTotal: b.descuentoTotal || null,
    ubicacion: typeof b.ubicacion === 'string' ? b.ubicacion : '',
    ubicacionElegida: !!b.ubicacionElegida,
    guardado: b.guardado || null,
  };
}

/** Se llama al cobrar y al vaciar el carrito. */
export function borrarBorradorVenta(usuario) {
  const llave = llaveBorradorVenta(usuario);
  const caja = almacen();
  if (!llave || !caja) return;
  try { caja.removeItem(llave); } catch { /* da igual */ }
}

/** El aviso que se enseña al recuperar la venta. */
export function textoVentaRecuperada(n) {
  return n === 1
    ? 'Se recuperó la venta en curso: 1 producto en el carrito.'
    : `Se recuperó la venta en curso: ${n} productos en el carrito.`;
}
